import { Banner, Button, Dialog, DialogButtons, LinkIcon, Modal } from '@st/theme'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { selKarbonDeliveryDialog } from './document-viewer-extensions/tax-return-delivery/state'

/** Renders the open Karbon delivery dialog from folder state. */
export function KarbonDeliveryModal() {
  const folder = useProcess('stFolder')
  const dialog = useSelect('stFolder', selKarbonDeliveryDialog)
  if (!dialog) return null

  return (
    <Modal isOpen={true}>
      <KarbonDeliveryDialog
        url={dialog.url}
        publication={dialog.publication}
        onCopyLink={() => folder.send({ type: 'karbonLinkCopyRequested', url: dialog.url })}
        onPublish={() => folder.send({ type: 'requestEnqueued', request: dialog.publish })}
        onClose={() => folder.send({ type: 'karbonDeliveryClosed' })}
      />
    </Modal>
  )
}

/**
 * Delivery for firms whose client email is managed by Karbon. StanfordTax sends
 * no email: staff publish the return to the client's StanfordTax dashboard and
 * share the client link through Karbon themselves. Copying and publishing are
 * independent; the dialog stays open after publishing so the link can still be
 * copied.
 */
function KarbonDeliveryDialog({
  url,
  publication,
  onCopyLink,
  onPublish,
  onClose
}: {
  /** The client's organizer link, which signs them in through Karbon. */
  url: string
  publication: 'unpublished' | 'publishing' | 'published'
  onCopyLink: () => void
  onPublish: () => void
  onClose: () => void
}) {
  return (
    <Dialog
      title="Deliver tax return"
      className="w-full max-w-2xl"
      buttons={
        <DialogButtons>
          <Button variant="subtle" disabled={publication == 'publishing'} onClick={onClose}>
            Close
          </Button>
          {match(publication)
            .with('unpublished', () => (
              <Button variant="primary" onClick={onPublish}>
                Publish
              </Button>
            ))
            .with('publishing', () => (
              <Button variant="primary" disabled>
                Publishing…
              </Button>
            ))
            .with('published', () => null)
            .exhaustive()}
        </DialogButtons>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          Your account is connected to Karbon. Sending tax return delivery emails through Karbon is
          on our roadmap for the upcoming tax season.
        </p>
        {match(publication)
          .with('published', () => (
            <Banner variant="success" className="text-left text-sm">
              Published to your client’s StanfordTax dashboard. Copy the link below and share it
              through Karbon.
            </Banner>
          ))
          .with(P.union('unpublished', 'publishing'), () => (
            <p>
              For now, publish the tax return to make it available in your client’s StanfordTax
              dashboard, then copy the link below and share it with your client through Karbon.
            </p>
          ))
          .exhaustive()}
        <label className="block space-y-1">
          <span className="font-semibold">Karbon client link</span>
          <span className="flex items-center gap-2">
            <input
              className="min-w-0 flex-1 rounded border border-gray-200 px-3 py-2"
              readOnly
              value={url}
            />
            <Button
              variant="default"
              leadingIcon={<LinkIcon className="size-4" />}
              onClick={onCopyLink}
            >
              Copy link
            </Button>
          </span>
        </label>
        {match(publication)
          .with(P.union('unpublished', 'publishing'), () => (
            <p className="text-gray-500">
              Publishing adds the tax return to your client’s StanfordTax dashboard. It won’t send
              an email to your client.
            </p>
          ))
          .with('published', () => null)
          .exhaustive()}
      </div>
    </Dialog>
  )
}
